import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import {
  MfaSetupResponse,
  GenerateRecoveryCodesResponse,
} from '@classpass/protobuf-definitions/lib/web/models/partners/user_mfa';

import Message from 'components/Message';
import { Card, CardTitle } from 'components/Card';
import Button from 'components/Button';
import When from 'components/When';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { openModal, closeModal } from 'components/Modal';
import LoadingDots from 'components/LoadingDots';

import { bem } from 'helpers/class-name';
import { httpClient } from 'helpers/http';
import { reportError } from 'helpers/error-logging';
import URLS from 'constants/urls';

import { messages } from './MFASection.messages';
import './MFASection.scss';

const { element, modifier } = bem('mfa-section');

export const MFASection = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchMFAStatus = async () => {
      setIsLoading(true);
      try {
        const { data: mfaStatusFlag } = await httpClient.get(URLS.user.mfa.checkMFAStatus);
        setIsEnabled(mfaStatusFlag);
      } catch (error) {
        reportError('Error fetching MFA status', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMFAStatus();
  }, []);

  const handleEnableMFA = async () => {
    setIsLoading(true);
    try {
      const { data } = await httpClient.post(URLS.user.mfa.enableMFA);
      const mfaData = MfaSetupResponse.fromJSON(data);
      dispatch(
        openModal('MFASetupModal', {
          privateKey: mfaData.private_key,
          qrcodeURI: mfaData.private_qr_code_uri,
          onMFAActivatedSucessfully: () => setIsEnabled(true),
          shouldCloseOnOverlayClick: false,
          // eslint-disable-next-line no-use-before-define
          onRestartSetup: handleRestartSetup,
        })
      );
    } catch (error) {
      reportError('Error enabling MFA', error);
      dispatch(createErrorSnackbar(messages.errorEnableMFA));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    dispatch(closeModal());
    setIsLoading(true);
    try {
      await httpClient.patch(URLS.user.mfa.disableMFA);
      dispatch(createSuccessSnackbar(messages.successDisablingMFA));
      setIsEnabled(false);
    } catch (error) {
      reportError('Error disabling MFA', error);
      dispatch(createErrorSnackbar(messages.errorDisablingMFA));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableMFAConfirmation = () => {
    dispatch(
      openModal('ConfirmationModal', {
        title: messages.disableMfaConfirmationTitle,
        message: messages.disableMfaConfirmationMessage,
        confirmButtonText: messages.disableMfaConfirmationBtn,
        confirmButtonStyle: 'danger-solid',
        onConfirm: handleDisableMFA,
      })
    );
  };

  const handleRestartSetup = () => {
    dispatch(closeModal());
    handleEnableMFA();
  };

  const handleGenerateNewRecoveryCodes = async () => {
    setIsLoading(true);
    try {
      const { data } = await httpClient.post(URLS.user.mfa.generateRecoveryCodes);
      const recoveryData = GenerateRecoveryCodesResponse.fromJSON(data);
      dispatch(
        openModal('MFARecoveryCodesModal', {
          recoveryCodes: recoveryData.private_recovery_codes,
        })
      );
      dispatch(createSuccessSnackbar(messages.successGeneratingRecoveryCodes));
    } catch (error) {
      reportError('Error generating new recovery codes', error);
      dispatch(createErrorSnackbar(messages.errorGeneratingRecoveryCodes));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <a className={element('anchor')} id="mfa-config" />
      }
      <Card>
        <Message as={CardTitle} message={messages.title} />
        <div>
          <Message message={messages.subtitle} />
          <div>
            <div className={element('content')}>
              <When predicate={!isLoading}>
                <div className={element('status-message-container')}>
                  <span
                    className={classNames(
                      element('status-message-icon'),
                      modifier(isEnabled ? 'icon-enabled' : 'icon-disabled')
                    )}
                    role="img"
                    aria-label={isEnabled ? 'MFA Status: Enabled' : 'MFA Status: Disabled'}
                  />
                  <Message message={isEnabled ? messages.enabled : messages.disabled} />
                </div>
              </When>
              <When predicate={isLoading}>
                <LoadingDots />
              </When>
              <When predicate={isEnabled}>
                <Button styled="danger-solid" waiting={isLoading} onClick={handleDisableMFAConfirmation}>
                  <Message message={messages.disableAction} />
                </Button>
              </When>
              <When predicate={!isEnabled}>
                <Button styled="primary" waiting={isLoading} onClick={handleEnableMFA}>
                  <Message message={messages.enableAction} />
                </Button>
              </When>
            </div>
            <When predicate={isEnabled}>
              <button
                className={element('recovery-link')}
                type="button"
                disabled={isLoading}
                onClick={handleGenerateNewRecoveryCodes}>
                <Message message={messages.recoveryLink} />
              </button>
            </When>
          </div>
        </div>
      </Card>
    </>
  );
};

export default MFASection;
